<template>
    <div ref="dialogRef" class="punchout-dialog" data-testid="punchout-dialog">
        <VJoyPanel padding="large">
            <template #panel-title>
                {{ content.title }}
            </template>
            <template v-if="content.subtitle" #panel-subtitle>
                <span class="punchout-dialog__subtitle">
                    {{ content.subtitle }}
                </span>
            </template>
            <template #panel-body>
                <VJoyHighlight :display-icon="true" :level="isSessionActive ? 'success' : 'warning'">
                    <template #default>
                        {{ content.description }}
                    </template>
                </VJoyHighlight>
                <VJoyButton
                    v-if="content.cta"
                    class="punchout-dialog__cta"
                    :label="content.cta"
                    size="small"
                    variant="main"
                    :href="`${punchoutDetails?.procurementAppUrl}`"
                    target="_blank"
                    data-testid="punchout-dialog-cta"
                />
            </template>
        </VJoyPanel>
    </div>
</template>

<script setup lang="ts">
    import {computed, ref, useTemplateRef} from 'vue';
    import type {PunchoutDetails} from '@navbar-api';
    import {useTranslation} from '#imports';
    import {onClickOutside} from '@malt/utils';
    import {VJoyButton, VJoyHighlight, VJoyPanel} from '@maltjoy/core-vue';

    interface DialogContent {
        title: string;
        subtitle?: string;
        description: string;
        cta?: string;
    }

    const props = defineProps<{
        punchoutDetails?: PunchoutDetails;
        isSessionActive?: boolean;
    }>();

    const {t} = useTranslation();
    const emit = defineEmits<{dialogClosed: []}>();

    const baseKey = `navbar.punchout.badge` as const;
    const appName = computed(() => props.punchoutDetails?.procurementAppName);

    const content = ref<DialogContent>(
        props.isSessionActive
            ? {
                  title: t(`${baseKey}.connected.title`, {0: appName.value}),
                  description: t(`${baseKey}.connected.content`, {0: appName.value, 1: appName.value}),
              }
            : {
                  title: t(`${baseKey}.disconnected.title`, {0: appName.value}),
                  subtitle: t(`${baseKey}.disconnected.subtitle`, {0: appName.value}),
                  description: t(`${baseKey}.disconnected.content`),
                  cta: t(`${baseKey}.disconnected.cta`, {0: appName.value}),
              },
    );

    const dialogRef = useTemplateRef('dialogRef');

    onClickOutside(dialogRef, () => emit('dialogClosed'), {ignore: ['.punchout-header']});
</script>

<style lang="scss" scoped>
    .punchout-dialog {
        --panel-bottom-margin: 0px;

        position: absolute;
        min-width: 530px;
        right: var(--joy-core-spacing-4);
        top: calc(var(--header-menu-height) - var(--joy-core-spacing-2));

        &__cta.joy-button {
            margin-top: var(--joy-core-spacing-8);
        }

        &__subtitle {
            font-size: var(--joy-font-size-primary-400);
        }

        .joy-panel {
            border: none;
            box-shadow: var(--joy-core-elevation-4);
        }
    }
</style>
